import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBottomTab } from '@/shared/contexts/bottom-tab-context';

/** Lugar, dentro do rodapé fixo das abas, logo acima delas (PersistentBottomTab). */
export const BOTTOM_ACTION_SLOT_ID = 'bottom-tab-action-slot';

/**
 * Barra de ação fixa no rodapé (PRAT-154), por exemplo o "Ver sacola".
 *
 * Antes a barra ficava a 72px do fundo e as abas por cima dela: no iPhone (área
 * segura embaixo) e com o Clube na barra, as abas cresciam e cobriam o botão —
 * o toque caía no Clube. Agora ela entra no mesmo bloco fixo das abas, logo
 * acima delas, e nunca fica por baixo de nada. Sem abas na tela, fica sozinha
 * no rodapé. Deixa no fim da página um espaço da altura da barra, para o
 * conteúdo não sumir atrás dela.
 *
 * O slot vem do contexto (o rodapé das abas registra o nó via ref), e não de
 * uma busca no DOM: a busca guardava um nó que podia já ter saído da tela (a
 * rota lazy suspende e os efeitos religam), e a barra ficava num portal para
 * um elemento solto — sem "Avançar" nem banner na primeira entrada da revisão
 * do pedido (PRAT-184).
 */
export function BottomActionBar({ children }: { children: ReactNode }) {
  const { hideBottomTab, actionSlot } = useBottomTab();
  const [spacer, setSpacer] = useState(0);

  const slot = !hideBottomTab && actionSlot ? actionSlot : null;

  const bar = (
    <div className="bg-white px-4 divide-y divide-gray-100 border-t border-gray-100 shadow-[0_-4px_12px_-8px_rgba(0,0,0,0.12)]">
      {children}
    </div>
  );

  // O AppLayout já reserva o espaço das abas; aqui entra só a altura da barra.
  useEffect(() => {
    const target = slot ?? document.getElementById('bottom-action-bar-solo');
    if (!target) return;
    const update = () => setSpacer(target.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(target);
    return () => ro.disconnect();
  }, [slot]);

  return (
    <>
      <div aria-hidden style={{ height: spacer }} />
      {slot ? (
        createPortal(bar, slot)
      ) : (
        <div
          id="bottom-action-bar-solo"
          className="fixed bottom-0 left-0 right-0 z-[60] pb-[env(safe-area-inset-bottom)] bg-white"
        >
          {bar}
        </div>
      )}
    </>
  );
}
